import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, Info, ShieldAlert } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet';
import { cn } from '@/lib/utils';
import { guidePage } from '@/config/guide/registry';
import {
  fieldDefKey,
  fieldLabelKey,
  fieldNegKey,
  fieldPosKey,
  pageCautionsKey,
  pagePurposeKey,
  pageSourceKey,
  pageSubtitleKey,
  pageTitleKey,
  pageWarningsKey,
  type FieldDoc,
} from './guide-types.js';

type TFn = (key: string, options?: Record<string, unknown>) => unknown;

/**
 * Read a bounded string list out of the catalog.
 *
 * i18next returns the raw array with `returnObjects`; a stubbed `t` (unit
 * tests echo the key) returns a string, hence the guard. `count` bounds the
 * result so a translator who appended an extra entry cannot slip an
 * un-reviewed warning onto a money screen.
 */
function stringList(t: TFn, key: string, count: number): string[] {
  if (count <= 0) return [];
  const raw = t(key, { returnObjects: true });
  if (!Array.isArray(raw)) return [];
  return raw.slice(0, count).map(String);
}

const KIND_TONE: Record<FieldDoc['kind'], string> = {
  'money-signed': 'bg-sky-500/10 text-sky-700 dark:text-sky-300',
  'money-unsigned': 'bg-muted text-muted-foreground',
  count: 'bg-muted text-muted-foreground',
  percent: 'bg-muted text-muted-foreground',
  'basis-points': 'bg-amber-500/10 text-amber-700 dark:text-amber-300',
  status: 'bg-muted text-muted-foreground',
  score: 'bg-muted text-muted-foreground',
  date: 'bg-muted text-muted-foreground',
  text: 'bg-muted text-muted-foreground',
};

function FieldEntry({ pageId, field }: { pageId: string; field: FieldDoc }) {
  const { t } = useTranslation('guide');
  return (
    <div className="space-y-1 border-l-2 border-border pl-3">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-sm font-medium">{t(fieldLabelKey(pageId, field.key))}</span>
        <span
          className={cn(
            'rounded px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide',
            KIND_TONE[field.kind],
          )}
        >
          {t(`common.kind.${field.kind}`)}
        </span>
      </div>
      <p className="text-sm leading-relaxed text-muted-foreground">
        {t(fieldDefKey(pageId, field.key))}
      </p>
      {field.formula ? (
        <code className="inline-block rounded bg-muted px-1.5 py-0.5 text-xs tabular-nums">
          {field.formula}
        </code>
      ) : null}
      {field.kind === 'money-signed' ? (
        <ul className="space-y-0.5 text-sm">
          <li className="flex gap-2">
            <span className="w-4 shrink-0 font-semibold text-emerald-600 dark:text-emerald-400">+</span>
            <span className="text-muted-foreground">{t(fieldPosKey(pageId, field.key))}</span>
          </li>
          <li className="flex gap-2">
            <span className="w-4 shrink-0 font-semibold text-destructive">−</span>
            <span className="text-muted-foreground">{t(fieldNegKey(pageId, field.key))}</span>
          </li>
        </ul>
      ) : null}
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="space-y-2">
      <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{title}</h3>
      {children}
    </section>
  );
}

/**
 * The page-level `i`: what this screen is for, what every figure means, which
 * direction of a sign favours whom, where the data comes from, what will
 * surprise the reader, and which controls move real money.
 */
export function PageGuide({ pageId }: { pageId: string }) {
  const { t } = useTranslation('guide');
  const page = guidePage(pageId);
  if (!page) return null;

  const cautions = stringList(t as TFn, pageCautionsKey(pageId), page.cautionCount);
  const warnings = stringList(t as TFn, pageWarningsKey(pageId), page.warningCount);

  return (
    <Sheet>
      <SheetTrigger asChild>
        <Button type="button" variant="outline" size="sm">
          <Info className="mr-1.5 h-4 w-4" />
          {t('common.button')}
        </Button>
      </SheetTrigger>
      <SheetContent side="right" className="w-full overflow-y-auto sm:max-w-xl">
        <SheetHeader>
          <SheetTitle>{t(pageTitleKey(pageId))}</SheetTitle>
          <SheetDescription>{t(pageSubtitleKey(pageId))}</SheetDescription>
        </SheetHeader>

        <div className="mt-6 space-y-6 pb-10">
          {/*
            The status note also lives here, not only in the on-page banner.
            The parity test requires one for every non-live page, but a screen
            rendered inside a drawer or dialog often has nowhere to put a
            banner — so without this the required copy could be written,
            reviewed and translated into seven languages and still reach
            nobody. The sheet is the one surface every documented page has.
          */}
          {page.status !== 'live' ? (
            <div className="flex items-start gap-2 rounded-md border border-amber-500/40 bg-amber-500/10 p-3 text-sm">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-500" aria-hidden />
              <span>
                <span className="font-medium">
                  {t(page.status === 'records-intent' ? 'common.recordsIntent' : 'common.partiallyWired')}
                </span>{' '}
                <span className="text-muted-foreground">{t(`${pageId}.statusNote`)}</span>
              </span>
            </div>
          ) : null}

          <Section title={t('common.sections.purpose')}>
            <p className="whitespace-pre-line text-sm leading-relaxed">{t(pagePurposeKey(pageId))}</p>
          </Section>

          {warnings.length > 0 ? (
            <Section title={t('common.sections.warnings')}>
              <ul className="space-y-2">
                {warnings.map((text) => (
                  <li
                    key={text}
                    className="flex gap-2 rounded-md border border-destructive/40 bg-destructive/5 p-2 text-sm leading-relaxed"
                  >
                    <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0 text-destructive" aria-hidden />
                    <span>{text}</span>
                  </li>
                ))}
              </ul>
            </Section>
          ) : null}

          <Section title={t('common.sections.fields')}>
            <div className="space-y-4">
              {page.fields.map((field) => (
                <FieldEntry key={field.key} pageId={pageId} field={field} />
              ))}
            </div>
          </Section>

          <Section title={t('common.sections.source')}>
            <p className="whitespace-pre-line text-sm leading-relaxed text-muted-foreground">
              {t(pageSourceKey(pageId))}
            </p>
          </Section>

          {cautions.length > 0 ? (
            <Section title={t('common.sections.cautions')}>
              <ul className="space-y-2">
                {cautions.map((text) => (
                  <li key={text} className="flex gap-2 text-sm leading-relaxed">
                    <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-500" aria-hidden />
                    <span>{text}</span>
                  </li>
                ))}
              </ul>
            </Section>
          ) : null}
        </div>
      </SheetContent>
    </Sheet>
  );
}
